import { ReactNode, useMemo, useState } from "react";
import styles from "./AttendeesTable.module.scss";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import classNames from "classnames";
import { useTranslation } from "../../i18n";

interface HeadCell {
  id: string;
  labelKey: string;
}

const headCells: HeadCell[] = [
  {
    id: "name",
    labelKey: "attendees.colName",
  },
  {
    id: "date",
    labelKey: "attendees.colDate",
  },
  {
    id: "status",
    labelKey: "attendees.colStatus",
  },
  {
    id: "action",
    labelKey: "attendees.colAction",
  },
];

interface Attendee {
  id: string | number;
  name: string;
  email: string;
  date: string;
  status?: string;
  action?: ReactNode;
  profileImage?: string;
  [key: string]: any;
}

type Order = "asc" | "desc";

const compare = (
  a: Attendee,
  b: Attendee,
  orderBy: string,
  order: Order = "asc",
): number => {
  const valueA = a[orderBy];
  const valueB = b[orderBy];

  if (valueA < valueB) return order === "asc" ? -1 : 1;
  if (valueA > valueB) return order === "asc" ? 1 : -1;
  return 0;
};

interface TableToolbarProps {
  attendees: Attendee[];
  numSelected: number;
}

const TableToolbar = ({ attendees, numSelected }: TableToolbarProps) => {
  const { t } = useTranslation();

  return (
    <div
      className={classNames(styles.toolbar, {
        [styles.active]: numSelected > 0,
      })}
    >
      {numSelected > 0 ? (
        <h4>{t("attendees.selected", { count: numSelected })}</h4>
      ) : (
        <h4>{t("attendees.title", { count: attendees.length })}</h4>
      )}
    </div>
  );
};

interface EnhancedTableHeadProps {
  rowCount: number;
  numSelected: number;
  order: Order;
  orderBy: string;
  onSortChange: (column: string) => void;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const EnhancedTableHead = ({
  rowCount,
  numSelected,
  order,
  orderBy,
  onSortChange,
  onSelectAllClick,
}: EnhancedTableHeadProps) => {
  const { t } = useTranslation();

  return (
    <TableHead className={styles.head}>
      <TableRow>
        <TableCell padding="none">
          <Checkbox
            color="primary"
            indeterminate={numSelected > 0 && numSelected < rowCount}
            checked={rowCount > 0 && numSelected === rowCount}
            onChange={onSelectAllClick}
          />
        </TableCell>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            sortDirection={orderBy === headCell.id ? order : false}
            padding="none"
            className={styles.cell}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={() => onSortChange(headCell.id)}
            >
              {t(headCell.labelKey)}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc"
                    ? t("attendees.sortedDesc")
                    : t("attendees.sortedAsc")}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

interface AttendeesTableProps {
  attendees: Attendee[];
  onExportDocs?: (selectedIds: string[]) => void;
  isExportingDocs?: boolean;
}

export const AttendeesTable = ({
  attendees,
  onExportDocs,
  isExportingDocs = false,
}: AttendeesTableProps) => {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>("name");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const handleSortChange = (column: string) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  };

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = attendees.map((attendee) => attendee.id);
      setSelected(newSelected);
    } else {
      setSelected([]);
    }
  };

  const handleCheckboxToggle = (id: string | number) => {
    setSelected((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((item) => item !== id)
        : [...prevSelected, id],
    );
  };

  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleData = useMemo(() => {
    return [...attendees]
      .sort((a, b) => compare(a, b, orderBy, order))
      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
  }, [attendees, order, orderBy, page, rowsPerPage]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - attendees.length) : 0;

  return (
    <>
      <TableToolbar attendees={attendees} numSelected={selected.length} />
      <TableContainer className={styles.tableContainer}>
        <Table size="small" className={styles.table}>
          <EnhancedTableHead
            rowCount={attendees.length}
            numSelected={selected.length}
            order={order}
            orderBy={orderBy}
            onSortChange={handleSortChange}
            onSelectAllClick={handleSelectAllClick}
          />
          <TableBody className={styles.body}>
            {visibleData.map((item) => {
              const isItemSelected = selected.includes(item.id);
              return (
                <TableRow
                  key={item.id}
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  onClick={() => handleCheckboxToggle(item.id)}
                  selected={isItemSelected}
                  hover
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="none">
                    <Checkbox color="primary" checked={isItemSelected} />
                  </TableCell>
                  <TableCell
                    padding="none"
                    className={classNames(styles.cell, styles.flex)}
                  >
                    <Avatar
                      alt="avatar"
                      src={item.profileImage}
                      sx={{ width: 32, height: 32 }}
                    >
                      {!item.profileImage &&
                        item.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <div className={styles.nameWrapper}>
                      <p>{item.name}</p>
                      <p>{item.email}</p>
                    </div>
                  </TableCell>
                  <TableCell padding="none" className={styles.cell}>
                    {item.date}
                  </TableCell>
                  <TableCell padding="none" className={styles.cell}>
                    {/* {item.status} */}
                    {t("orgEvent.readyToSign")}
                  </TableCell>
                  <TableCell padding="none" className={styles.cell}>
                    {item.action}
                  </TableCell>
                </TableRow>
              );
            })}
            {emptyRows > 0 && (
              <TableRow
                style={{
                  height: 53 * emptyRows,
                }}
              >
                <TableCell colSpan={5} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        <div className={styles.actions}>
          <TablePagination
            component="div"
            count={attendees.length}
            page={page}
            rowsPerPage={rowsPerPage}
            labelRowsPerPage={t("attendees.rowsPerPage")}
            rowsPerPageOptions={[10, 25, 50]}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            className={styles.pagination}
          />
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={selected.length === 0 || isExportingDocs}
            onClick={() => onExportDocs?.(selected.map(String))}
            startIcon={isExportingDocs ? <CircularProgress size={14} /> : null}
          >
            {isExportingDocs
              ? t("attendees.downloading")
              : selected.length > 0
                ? t("attendees.downloadDocs", { count: selected.length })
                : t("attendees.selectAttendees")}
          </Button>
        </div>
      </TableContainer>
    </>
  );
};
